/**
 * Загрузка профиля из Битрикс24 и локальное состояние карточки.
 *
 * Профиль читается и пишется напрямую в поле сделки: серверная часть
 * в этом не участвует, поэтому её недоступность не приводит к потере данных
 * (ТЗ п. 8.2).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createEmptyProfile, computeReadiness, computeTotals, type CaseProfile } from '@bd/domain';
import { linkedContact, type CaseContext } from '../../bx24.js';
import { api } from '../../api.js';
import { extractProfile, loadCase, saveProfile as saveToB24, CASE_FIELDS, type CaseItem } from '../../storage/cases.js';
import {
  applyToProfile,
  contactValues,
  dealTitleValues,
  mergeSnapshots,
  readCrmSnapshot,
  type CrmSnapshot,
  type FoundValue,
} from '../../storage/crm-import.js';

/** Профиль из карточки либо пустой — для нового дела. */
function stored(item: CaseItem | null, caseId: string): CaseProfile {
  return extractProfile(item) ?? createEmptyProfile(caseId);
}

export interface ProfileState {
  profile: CaseProfile | null;
  loading: boolean;
  error: string | null;
  /** Есть несохранённые изменения */
  dirty: boolean;
  /** Идентификатор папки дела на Диске, если он уже записан в карточке */
  folderId?: string;
  /** Карточка портала целиком: из неё берётся связанная сделка (FR-06) */
  item?: Record<string, unknown> | null;
  /**
   * Что подставлено из карточки сделки при открытии нового дела.
   * Показывается юристу для проверки: отвечает за документы он.
   */
  prefilled?: FoundValue[];
  /**
   * Все значения, которые портал может дать делу, с указанием источника.
   * Читается и для уже заполненного дела: по нему полоса предлагает
   * перенести то, чего в деле ещё нет.
   */
  crm?: CrmSnapshot;
}

/**
 * Что оказывается в карточке после того, как сохранение вернулось.
 *
 * Сохранение запускается при каждом уходе фокуса с поля и занимает секунду-две:
 * это обращение к порталу. Всё это время юрист продолжает работать — отмечает
 * галочками собранные документы, правит суммы.
 *
 * Раньше по возвращении в состояние записывался СНИМОК, сделанный перед
 * отправкой. Правки, сделанные за эти секунды, исчезали с экрана, а признак
 * «есть несохранённые изменения» снимался — то есть они не попадали и в
 * следующее сохранение. Со стороны: «галочки убираются произвольно», причём
 * произвольность зависела от того, попало ли нажатие в окно сохранения.
 *
 * Теперь снимок сравнивается с тем, что в карточке сейчас:
 *
 * — не изменилось: записывается сохранённая редакция, дело считается
 *   сохранённым;
 * — изменилось: правки остаются на месте, от сохранённой редакции берутся
 *   только её номер и отметки о последнем изменении, а дело остаётся
 *   несохранённым — следующее сохранение запишет и эти правки.
 */
export function mergeSavedRevision(
  current: CaseProfile | null,
  snapshot: CaseProfile,
  saved: CaseProfile,
): { profile: CaseProfile; dirty: boolean } {
  // Сравнение по ссылке: правки никогда не меняют профиль на месте,
  // они всегда создают новый объект
  if (current === snapshot || current === null) return { profile: saved, dirty: false };

  return {
    profile: { ...current, revision: saved.revision, meta: saved.meta },
    dirty: true,
  };
}

export function useProfile(context: CaseContext) {
  const [state, setState] = useState<ProfileState>({ profile: null, loading: true, error: null, dirty: false });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const item = await loadCase(context.caseId).catch(() => null);
        if (cancelled) return;

        let profile = stored(item, context.caseId);
        let prefilled: FoundValue[] = [];

        /**
         * Новое дело предзаполняется тем, что уже введено в карточке сделки:
         * менеджер заполнил чек-лист, и вводить то же самое второй раз
         * бессмысленно, а главное вредно — два ввода дают два разных ИНН.
         *
         * Только новое дело и только пустые поля: введённое юристом
         * не перезаписывается (FR-06).
         */
        /**
         * Снимок портала собирается ОДИН раз и здесь же — из чек-листа сделки
         * и из карточки контакта.
         *
         * Раньше контакт подставлялся отдельно и молча, а полоса показывала
         * только чек-лист: юрист читал «подставлено: ИНН», когда из контакта
         * пришли ещё и фамилия, имя, отчество. Проверить их было негде.
         *
         * Порядок источников значим: чек-лист впереди контакта. В чек-лист
         * менеджер вносит данные по паспорту, а в контакте ФИО живёт своей
         * жизнью — сокращения, девичья фамилия, «Ольга (бухгалтер)».
         * В заявлении должно стоять паспортное написание.
         */
        const [dealSnapshot, contact] = await Promise.all([
          readCrmSnapshot(context.entityTypeId, context.caseId, item),
          linkedContact(item),
        ]);
        if (cancelled) return;

        const fromSources = mergeSnapshots(dealSnapshot, {
          found: contact ? contactValues(contact) : [],
          skipped: [],
        });

        /**
         * Название сделки — запасной источник ФИО, последний по очереди.
         *
         * Сделки у заказчика называют по должнику, а карточку контакта
         * заполняют небрежно: встречается одно имя без фамилии. Фамилия при
         * этом стоит в заголовке сделки, и не взять её оттуда — значит
         * заставить юриста перепечатывать то, что видно на экране.
         *
         * Берётся только с подтверждением: одно из слов названия должно
         * совпасть с частью ФИО из настоящего источника. Иначе «Повторная
         * сделка» стала бы фамилией и именем должника.
         */
        const snapshot = mergeSnapshots(fromSources, {
          found: dealTitleValues(item?.title, fromSources.found),
          skipped: [],
        });

        // Заполняется только новое дело: в начатом решает юрист — полоса
        // предложит перенос кнопкой (FR-06)
        if (!extractProfile(item) && snapshot.found.length > 0) {
          const applied = applyToProfile(profile, snapshot.found);
          profile = applied.profile;
          prefilled = applied.filled;
        }

        if (cancelled) return;
        setState({
          profile,
          loading: false,
          error: null,
          // Предзаполненное нужно сохранить: иначе оно исчезнет при обновлении
          // страницы, и юрист решит, что приложение его потеряло
          dirty: prefilled.length > 0,
          folderId: (item?.[CASE_FIELDS.folderId] as string) || undefined,
          item,
          prefilled,
          crm: snapshot,
        });
      } catch (error) {
        if (cancelled) return;
        // Даже при ошибке чтения даём работать с пустым профилем: иначе
        // пользователь заперт в неработающей вкладке.
        setState({
          profile: createEmptyProfile(context.caseId),
          loading: false,
          error: (error as Error).message,
          dirty: false,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [context.caseId, context.entityTypeId]);

  /** Изменение профиля. Номер редакции растёт при сохранении, не при правке. */
  const update = useCallback((updater: (profile: CaseProfile) => CaseProfile) => {
    setState((current) => {
      if (!current.profile) return current;
      return { ...current, profile: updater(current.profile), dirty: true };
    });
  }, []);

  /**
   * Запись профиля в поле сделки и следом — резервная копия на сервере.
   *
   * Порядок именно такой: портал первичен, и если сервер недоступен, работа
   * не останавливается — теряется только возможность откатиться к предыдущей
   * редакции (ТЗ п. 5.1).
   */
  const save = useCallback(
    async (user?: { id: string; name: string }) => {
      const profile = state.profile;
      if (!profile) return null;

      const next: CaseProfile = {
        ...profile,
        revision: profile.revision + 1,
        meta: {
          ...profile.meta,
          updatedAt: new Date().toISOString(),
          updatedByUserId: user?.id ?? profile.meta.updatedByUserId,
        },
      };

      await saveToB24(context.caseId, next);
      setState((current) => ({ ...current, ...mergeSavedRevision(current.profile, profile, next) }));

      // Резервная копия и запись в журнал: сбой здесь не отменяет сохранения
      await api.backup(context.caseId, next).catch(() => undefined);

      return next;
    },
    [state.profile, context.caseId],
  );

  /**
   * Замена профиля целиком — восстановление из резервной копии (ТЗ п. 5.1).
   *
   * Номер редакции берётся от текущего профиля, а не от восстановленного:
   * иначе редакции пошли бы назад, и следующая копия перезаписала бы ту,
   * из которой только что восстановились.
   */
  const replace = useCallback(
    async (restored: CaseProfile, user?: { id: string; name: string }) => {
      const current = state.profile;
      const next: CaseProfile = {
        ...restored,
        caseId: context.caseId,
        revision: (current?.revision ?? restored.revision) + 1,
        meta: {
          ...restored.meta,
          updatedAt: new Date().toISOString(),
          updatedByUserId: user?.id ?? restored.meta.updatedByUserId,
        },
      };

      await saveToB24(context.caseId, next);
      setState((currentState) => ({ ...currentState, profile: next, dirty: false }));
      await api.backup(context.caseId, next).catch(() => undefined);
      return next;
    },
    [state.profile, context.caseId],
  );

  /** Проверки считаются здесь же: тот же код, что и перед сборкой документа. */
  const readiness = useMemo(
    () => (state.profile ? computeReadiness(state.profile) : null),
    [state.profile],
  );

  const totals = useMemo(() => (state.profile ? computeTotals(state.profile) : null), [state.profile]);

  return { ...state, update, save, replace, readiness, totals, setState };
}
